class MaisonProduct {
  constructor(root) {
    this.root = root;
    this.gallery = root.querySelector('[data-product-gallery]');
    this.slides = [...root.querySelectorAll('[data-media-id]')];
    this.thumbnails = [...root.querySelectorAll('[data-product-thumbnail]')];
    this.current = root.querySelector('[data-current-media]');
    this.select = root.querySelector('[data-variant-select]');
    this.variantInput = root.querySelector('[data-variant-id]');
    this.bindGallery();
    this.bindMediaViewer();
    this.bindSmartMediaFit();
    this.bindVariant();
    this.bindQuantity();
    this.bindStickyPurchase();
    this.bindGiftNote();
  }

  bindGallery() {
    this.thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', (event) => {
        event.preventDefault();
        this.showMedia(thumbnail.dataset.mediaTarget);
      });
    });
    if (!this.gallery || this.slides.length < 2) return;
    let queued = false;
    this.gallery.addEventListener('scroll', () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        const galleryCenter = this.gallery.scrollLeft + this.gallery.clientWidth / 2;
        let closestIndex = 0;
        let closestDistance = Infinity;
        this.slides.forEach((slide, index) => {
          const center = slide.offsetLeft + slide.offsetWidth / 2;
          const distance = Math.abs(center - galleryCenter);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });
        this.setActiveMedia(closestIndex);
        queued = false;
      });
    }, { passive: true });
  }

  showMedia(mediaId) {
    const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index < 0 || !this.gallery) return;
    this.gallery.scrollTo({ left: this.slides[index].offsetLeft, behavior: 'smooth' });
    this.setActiveMedia(index);
  }

  setActiveMedia(index) {
    if (this.current) this.current.textContent = String(index + 1);
    const activeId = this.slides[index]?.dataset.mediaId;
    this.thumbnails.forEach((thumbnail) => thumbnail.classList.toggle('is-active', thumbnail.dataset.mediaTarget === activeId));
  }

  bindMediaViewer() {
    this.zoomableSlides = [...this.root.querySelectorAll('[data-product-media-open]')];
    this.mediaModal = this.root.querySelector('[data-product-media-modal]');
    this.mediaModalImage = this.root.querySelector('[data-product-media-modal-image]');
    this.mediaModalViewport = this.root.querySelector('[data-product-media-modal-viewport]');
    this.mediaModalCurrent = this.root.querySelector('[data-product-media-modal-current]');
    this.mediaModalTotal = this.root.querySelector('[data-product-media-modal-total]');
    this.mediaModalIndex = 0;
    this.mediaModalTrigger = null;

    if (!this.zoomableSlides.length || !this.mediaModal || !this.mediaModalImage) return;

    if (this.mediaModalTotal) this.mediaModalTotal.textContent = String(this.zoomableSlides.length);

    this.zoomableSlides.forEach((slide, index) => {
      let pointerStart = null;

      slide.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        pointerStart = {
          id: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          scrollLeft: this.gallery?.scrollLeft || 0,
          time: performance.now()
        };
      }, { passive: true });

      slide.addEventListener('pointerup', (event) => {
        if (!pointerStart || pointerStart.id !== event.pointerId) return;
        const distanceX = Math.abs(event.clientX - pointerStart.x);
        const distanceY = Math.abs(event.clientY - pointerStart.y);
        const scrollDistance = Math.abs((this.gallery?.scrollLeft || 0) - pointerStart.scrollLeft);
        const duration = performance.now() - pointerStart.time;
        pointerStart = null;

        // Open only on a genuine tap/click. Horizontal swipes continue to control the gallery.
        if (distanceX <= 12 && distanceY <= 12 && scrollDistance <= 12 && duration <= 750) {
          event.preventDefault();
          this.openMediaViewer(index, slide);
        }
      });

      slide.addEventListener('pointercancel', () => { pointerStart = null; }, { passive: true });
      slide.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.openMediaViewer(index, slide);
        }
      });

      slide.querySelector('img')?.addEventListener('dragstart', (event) => event.preventDefault());
      slide.querySelector('img')?.addEventListener('contextmenu', (event) => {
        if (window.matchMedia('(pointer: coarse)').matches) event.preventDefault();
      });
    });

    this.root.querySelector('[data-product-media-close]')?.addEventListener('click', () => this.closeMediaViewer());
    this.root.querySelector('[data-product-media-previous]')?.addEventListener('click', () => this.showModalMedia(this.mediaModalIndex - 1));
    this.root.querySelector('[data-product-media-next]')?.addEventListener('click', () => this.showModalMedia(this.mediaModalIndex + 1));

    this.mediaModal.addEventListener('click', (event) => {
      if (event.target === this.mediaModal) this.closeMediaViewer();
    });

    this.handleMediaModalKeydown = (event) => {
      if (this.mediaModal.hidden) return;
      if (event.key === 'Escape') this.closeMediaViewer();
      if (event.key === 'ArrowLeft') this.showModalMedia(this.mediaModalIndex - 1);
      if (event.key === 'ArrowRight') this.showModalMedia(this.mediaModalIndex + 1);
    };
    document.addEventListener('keydown', this.handleMediaModalKeydown);

    let modalPointerStart = null;
    this.mediaModalViewport?.addEventListener('pointerdown', (event) => {
      modalPointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
    }, { passive: true });
    this.mediaModalViewport?.addEventListener('pointerup', (event) => {
      if (!modalPointerStart || modalPointerStart.id !== event.pointerId) return;
      const deltaX = event.clientX - modalPointerStart.x;
      const deltaY = event.clientY - modalPointerStart.y;
      modalPointerStart = null;
      if (Math.abs(deltaX) >= 45 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
        if (deltaX < 0) this.showModalMedia(this.mediaModalIndex + 1);
        else this.showModalMedia(this.mediaModalIndex - 1);
      }
    }, { passive: true });
    this.mediaModalViewport?.addEventListener('pointercancel', () => { modalPointerStart = null; }, { passive: true });
  }

  openMediaViewer(index, trigger) {
    if (!this.mediaModal || !this.mediaModalImage) return;
    this.mediaModalTrigger = trigger || this.zoomableSlides[index] || null;
    this.showModalMedia(index);
    this.mediaModal.hidden = false;
    document.documentElement.classList.add('product-media-modal-open');
    document.body.classList.add('product-media-modal-open');
    requestAnimationFrame(() => this.root.querySelector('[data-product-media-close]')?.focus({ preventScroll: true }));
  }

  closeMediaViewer() {
    if (!this.mediaModal || this.mediaModal.hidden) return;
    this.mediaModal.hidden = true;
    this.mediaModalImage.removeAttribute('src');
    document.documentElement.classList.remove('product-media-modal-open');
    document.body.classList.remove('product-media-modal-open');
    this.mediaModalTrigger?.focus({ preventScroll: true });
  }

  showModalMedia(index) {
    if (!this.zoomableSlides?.length || !this.mediaModalImage) return;
    const total = this.zoomableSlides.length;
    this.mediaModalIndex = (index + total) % total;
    const slide = this.zoomableSlides[this.mediaModalIndex];
    const sourceImage = slide.querySelector('img');
    const source = slide.dataset.zoomSrc || sourceImage?.src || sourceImage?.currentSrc;
    if (!source) return;

    this.mediaModalImage.src = source;
    this.mediaModalImage.alt = slide.dataset.zoomAlt || sourceImage?.alt || '';
    if (this.mediaModalCurrent) this.mediaModalCurrent.textContent = String(this.mediaModalIndex + 1);

    const previousButton = this.root.querySelector('[data-product-media-previous]');
    const nextButton = this.root.querySelector('[data-product-media-next]');
    const hideNavigation = total < 2;
    if (previousButton) previousButton.hidden = hideNavigation;
    if (nextButton) nextButton.hidden = hideNavigation;
  }

  bindSmartMediaFit() {
    const images = [...this.root.querySelectorAll('.product-media-image')];
    if (!images.length) return;

    const run = (image) => {
      if (!image.naturalWidth || !image.naturalHeight) return;
      this.applySmartMediaFit(image);
    };

    images.forEach((image) => {
      if (image.complete) run(image);
      else image.addEventListener('load', () => run(image), { once: true });
    });
  }

  applySmartMediaFit(image) {
    const configuredFit = getComputedStyle(this.root).getPropertyValue('--product-media-fit').trim();
    if (configuredFit === 'contain') {
      image.style.removeProperty('--smart-media-scale');
      image.style.removeProperty('--smart-media-position');
      return;
    }

    try {
      const sampleLimit = 72;
      const ratio = Math.min(sampleLimit / image.naturalWidth, sampleLimit / image.naturalHeight, 1);
      const width = Math.max(8, Math.round(image.naturalWidth * ratio));
      const height = Math.max(8, Math.round(image.naturalHeight * ratio));
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) return;
      context.drawImage(image, 0, 0, width, height);
      const pixels = context.getImageData(0, 0, width, height).data;

      const cornerRadius = Math.max(1, Math.round(Math.min(width, height) * 0.06));
      const cornerPixels = [];
      const collectCorner = (startX, startY) => {
        for (let y = startY; y < startY + cornerRadius; y += 1) {
          for (let x = startX; x < startX + cornerRadius; x += 1) {
            const index = (y * width + x) * 4;
            cornerPixels.push([pixels[index], pixels[index + 1], pixels[index + 2]]);
          }
        }
      };
      collectCorner(0, 0);
      collectCorner(width - cornerRadius, 0);
      collectCorner(0, height - cornerRadius);
      collectCorner(width - cornerRadius, height - cornerRadius);

      const background = [0, 1, 2].map((channel) =>
        cornerPixels.reduce((sum, pixel) => sum + pixel[channel], 0) / cornerPixels.length
      );
      const cornerDeviation = cornerPixels.reduce((sum, pixel) => {
        const difference = Math.sqrt(
          ((pixel[0] - background[0]) ** 2) +
          ((pixel[1] - background[1]) ** 2) +
          ((pixel[2] - background[2]) ** 2)
        );
        return sum + difference;
      }, 0) / cornerPixels.length;

      // Busy corners indicate a full-bleed photograph rather than a padded studio image.
      if (cornerDeviation > 34) return;

      const differenceThreshold = Math.max(30, cornerDeviation * 2.2);
      const rowHits = new Array(height).fill(0);
      const columnHits = new Array(width).fill(0);

      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const index = (y * width + x) * 4;
          if (pixels[index + 3] < 24) continue;
          const difference = Math.sqrt(
            ((pixels[index] - background[0]) ** 2) +
            ((pixels[index + 1] - background[1]) ** 2) +
            ((pixels[index + 2] - background[2]) ** 2)
          );
          if (difference > differenceThreshold) {
            rowHits[y] += 1;
            columnHits[x] += 1;
          }
        }
      }

      const minimumRowHits = Math.max(2, Math.round(width * 0.035));
      const minimumColumnHits = Math.max(2, Math.round(height * 0.035));
      let top = rowHits.findIndex((count) => count >= minimumRowHits);
      let bottom = rowHits.length - 1 - [...rowHits].reverse().findIndex((count) => count >= minimumRowHits);
      let left = columnHits.findIndex((count) => count >= minimumColumnHits);
      let right = columnHits.length - 1 - [...columnHits].reverse().findIndex((count) => count >= minimumColumnHits);

      if (top < 0 || left < 0 || bottom < top || right < left) return;

      const padX = Math.max(1, Math.round(width * 0.035));
      const padY = Math.max(1, Math.round(height * 0.035));
      left = Math.max(0, left - padX);
      right = Math.min(width - 1, right + padX);
      top = Math.max(0, top - padY);
      bottom = Math.min(height - 1, bottom + padY);

      const blankLeft = left / width;
      const blankRight = (width - 1 - right) / width;
      const blankTop = top / height;
      const blankBottom = (height - 1 - bottom) / height;
      const largestBlank = Math.max(blankLeft, blankRight, blankTop, blankBottom);
      const totalVerticalBlank = blankTop + blankBottom;
      const totalHorizontalBlank = blankLeft + blankRight;

      if (largestBlank < 0.075 && totalVerticalBlank < 0.12 && totalHorizontalBlank < 0.12) return;

      const centerX = ((left + right) / 2 / width) * 100;
      const centerY = ((top + bottom) / 2 / height) * 100;
      const zoom = Math.min(1.28, 1 + (largestBlank * 1.35));

      image.style.setProperty('--smart-media-scale', zoom.toFixed(3));
      image.style.setProperty('--smart-media-position', `${centerX.toFixed(1)}% ${centerY.toFixed(1)}%`);
      image.classList.add('is-smart-fitted');
    } catch (error) {
      // Cross-origin or canvas restrictions should never block the gallery.
      image.style.removeProperty('--smart-media-scale');
      image.style.removeProperty('--smart-media-position');
    }
  }

  bindVariant() {
    if (!this.select) return;
    this.select.addEventListener('change', () => {
      const option = this.select.selectedOptions[0];
      const available = option.dataset.available === 'true';
      if (this.variantInput) this.variantInput.value = option.value;
      this.root.querySelectorAll('[data-product-price]').forEach((price) => { price.textContent = option.dataset.price || ''; });
      this.root.querySelectorAll('[data-product-sticky-price]').forEach((price) => { price.textContent = option.dataset.price || ''; });
      const compare = this.root.querySelector('[data-compare-price]');
      const savings = this.root.querySelector('[data-product-savings]');
      const inventory = this.root.querySelector('[data-inventory-status]');
      if (compare) {
        compare.textContent = option.dataset.comparePrice || '';
        compare.classList.toggle('is-hidden', !option.dataset.comparePrice);
      }
      if (savings) {
        savings.textContent = option.dataset.savings ? `${option.dataset.savings}% off` : '';
        savings.classList.toggle('is-hidden', !option.dataset.savings);
      }
      if (inventory) {
        inventory.classList.toggle('inventory-status--out', !available);
        const label = inventory.querySelector('b');
        const showLowStock = inventory.dataset.showLowStock === 'true';
        const threshold = Number(inventory.dataset.lowStockThreshold || 0);
        const tracked = option.dataset.inventoryTracked === 'true';
        const qty = Number(option.dataset.inventoryQty || 0);
        const isLowStock = available && showLowStock && tracked && qty > 0 && qty <= threshold;
        inventory.classList.toggle('inventory-status--low', isLowStock);
        if (label) {
          if (isLowStock) {
            label.textContent = (inventory.dataset.lowStockText || '').replace('{count}', String(qty));
          } else if (available) {
            label.textContent = inventory.dataset.inStockText;
          } else {
            label.textContent = inventory.dataset.outOfStockText;
          }
        }
      }
      this.root.querySelectorAll('[data-add-to-cart]').forEach((button) => {
        button.disabled = !available;
        const text = button.querySelector('[data-add-text]');
        if (text) text.textContent = available ? button.dataset.addLabel : button.dataset.soldOutLabel;
      });
      if (option.dataset.mediaId) this.showMedia(option.dataset.mediaId);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', option.value);
      history.replaceState({}, '', url);
    });
  }

  bindQuantity() {
    const input = this.root.querySelector('[data-quantity-input]');
    if (!input) return;
    this.root.querySelector('[data-quantity-minus]')?.addEventListener('click', () => { input.value = String(Math.max(1, Number(input.value || 1) - 1)); });
    this.root.querySelector('[data-quantity-plus]')?.addEventListener('click', () => { input.value = String(Math.max(1, Number(input.value || 1) + 1)); });
  }

  bindGiftNote() {
    const toggle = this.root.querySelector('[data-gift-note-toggle]');
    const field = this.root.querySelector('[data-gift-note-field]');
    const input = this.root.querySelector('[data-gift-note-input]');
    if (!toggle || !field || !input) return;
    input.disabled = true;
    toggle.addEventListener('change', () => {
      field.classList.toggle('is-hidden', !toggle.checked);
      input.disabled = !toggle.checked;
      if (toggle.checked) input.focus();
      else input.value = '';
    });
  }

  bindStickyPurchase() {
    const stickyBar = this.root.querySelector('[data-mobile-product-bar]');
    const primaryButton = this.root.querySelector('.product-purchase-row [data-add-to-cart]');
    if (!stickyBar || !primaryButton || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(([entry]) => {
      stickyBar.classList.toggle('is-visible', !entry.isIntersecting && window.scrollY > 320);
    }, { threshold: 0.05 });
    observer.observe(primaryButton);
  }
}

document.querySelectorAll('[data-product-section]').forEach((root) => new MaisonProduct(root));
